import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles, Pencil, Languages } from "lucide-react";
import ConfirmDeleteButton from "@/components/ConfirmDeleteButton";

export default function Menu() {
  const { t, lang, localized } = useI18n();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCat, setSelectedCat] = useState("all");
  const [itemDialog, setItemDialog] = useState(false);
  const [catDialog, setCatDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/categories"), api.get("/menu-items")]);
    setCategories(c.data);
    setItems(i.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (selectedCat === "all" ? items : items.filter((i) => i.category_id === selectedCat)),
    [items, selectedCat]
  );

  const openNew = (cat_id = "") => {
    setEditing({
      category_id: cat_id || (categories[0]?.id ?? ""),
      name: { tr: "", de: "" },
      description: { tr: "", de: "" },
      price: 0,
      tax_rate: 20,
      available: true,
      allergens: [],
    });
    setItemDialog(true);
  };
  const openEdit = (it) => {
    setEditing({ ...it });
    setItemDialog(true);
  };
  const removeItem = async (id) => {
    try {
      await api.delete(`/menu-items/${id}`);
      await load();
      toast.success(lang === "tr" ? "Silindi" : "Gelöscht");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };
  const saveItem = async () => {
    try {
      const payload = {
        category_id: editing.category_id,
        name: editing.name,
        description: editing.description,
        price: parseFloat(editing.price) || 0,
        tax_rate: parseFloat(editing.tax_rate) || 20,
        image: editing.image || null,
        allergens: editing.allergens || [],
        available: !!editing.available,
      };
      if (editing.id) await api.put(`/menu-items/${editing.id}`, payload);
      else await api.post(`/menu-items`, payload);
      setItemDialog(false);
      setEditing(null);
      await load();
      toast.success(t("save"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const aiTranslate = async () => {
    if (!editing) return;
    setAiLoading(true);
    try {
      const source = lang;
      const target = lang === "tr" ? "de" : "tr";
      const nameSrc = editing.name?.[source] || "";
      const descSrc = editing.description?.[source] || "";
      const [n, d] = await Promise.all([
        nameSrc ? api.post("/ai/translate", { text: nameSrc, source, target }) : Promise.resolve({ data: { text: "" } }),
        descSrc ? api.post("/ai/translate", { text: descSrc, source, target }) : Promise.resolve({ data: { text: "" } }),
      ]);
      setEditing({
        ...editing,
        name: { ...editing.name, [target]: n.data.text },
        description: { ...editing.description, [target]: d.data.text },
      });
      toast.success(t("aiTranslate"));
    } catch (e) {
      toast.error("AI");
    } finally {
      setAiLoading(false);
    }
  };

  const aiDescribe = async () => {
    if (!editing?.name?.[lang]) return toast.error(lang === "tr" ? "Önce isim gir" : "Zuerst Name eingeben");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/describe", { name: editing.name[lang], lang });
      setEditing({ ...editing, description: { ...editing.description, [lang]: data.description } });
    } catch { toast.error("AI"); } finally { setAiLoading(false); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{t("menu")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} · {t("items")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatDialog(true)} data-testid="new-category-btn">
            <Plus className="w-4 h-4 mr-2" /> {t("newCategory")}
          </Button>
          <Button onClick={() => openNew()} disabled={categories.length === 0} data-testid="new-item-btn">
            <Plus className="w-4 h-4 mr-2" /> {t("newItem")}
          </Button>
        </div>
      </div>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <Chip active={selectedCat === "all"} onClick={() => setSelectedCat("all")}>
          {lang === "tr" ? "Tümü" : "Alle"} ({items.length})
        </Chip>
        {categories.map((c) => (
          <Chip
            key={c.id}
            active={selectedCat === c.id}
            onClick={() => setSelectedCat(c.id)}
          >
            {localized(c.name)} ({items.filter((i) => i.category_id === c.id).length})
          </Chip>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 border border-dashed rounded-lg text-muted-foreground">
            {t("empty")}
          </div>
        )}
        {filtered.map((it) => (
          <Card key={it.id} className="border shadow-none group">
            <CardContent className="p-4">
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{localized(it.name)}</div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    {localized(it.description) || "—"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-semibold text-primary" data-testid={`item-price-${it.id}`}>
                    {money(it.price)}
                  </div>
                  {!it.available && <Badge variant="secondary" className="mt-1 text-[10px]">off</Badge>}
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-1 opacity-70 group-hover:opacity-100">
                <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <ConfirmDeleteButton
                  onConfirm={() => removeItem(it.id)}
                  itemName={typeof it.name === "string" ? it.name : (it.name?.[lang] || it.name?.tr)}
                  data-testid={`del-item-${it.id}`}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Item Dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing?.id ? t("edit") : t("newItem")}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>{lang === "tr" ? "Kategori" : "Kategorie"}</Label>
                <Select
                  value={editing.category_id}
                  onValueChange={(v) => setEditing({ ...editing, category_id: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="item-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{localized(c.name)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ad (TR)</Label>
                  <Input value={editing.name.tr} onChange={(e) => setEditing({ ...editing, name: { ...editing.name, tr: e.target.value } })} data-testid="item-name-tr" className="mt-1" />
                </div>
                <div>
                  <Label>Name (DE)</Label>
                  <Input value={editing.name.de} onChange={(e) => setEditing({ ...editing, name: { ...editing.name, de: e.target.value } })} data-testid="item-name-de" className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Açıklama (TR)</Label>
                  <Textarea rows={2} value={editing.description.tr} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, tr: e.target.value } })} data-testid="item-desc-tr" className="mt-1" />
                </div>
                <div>
                  <Label>Beschreibung (DE)</Label>
                  <Textarea rows={2} value={editing.description.de} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, de: e.target.value } })} data-testid="item-desc-de" className="mt-1" />
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" type="button" onClick={aiTranslate} disabled={aiLoading} data-testid="ai-translate-btn">
                  <Languages className="w-4 h-4 mr-2" /> {t("aiTranslate")}
                </Button>
                <Button size="sm" variant="outline" type="button" onClick={aiDescribe} disabled={aiLoading} data-testid="ai-describe-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> {t("aiDescribe")}
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>{t("price")} (€)</Label>
                  <Input type="number" step="0.10" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="item-price" className="mt-1 font-mono" />
                </div>
                <div>
                  <Label>{t("taxRate")}</Label>
                  <Input type="number" step="1" value={editing.tax_rate} onChange={(e) => setEditing({ ...editing, tax_rate: e.target.value })} className="mt-1 font-mono" />
                </div>
                <div className="flex flex-col justify-end">
                  <Label className="mb-1">{t("available")}</Label>
                  <Switch checked={!!editing.available} onCheckedChange={(v) => setEditing({ ...editing, available: v })} data-testid="item-available" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(false)}>{t("cancel")}</Button>
            <Button onClick={saveItem} data-testid="save-item">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <CategoryDialog open={catDialog} onOpenChange={setCatDialog} onSaved={load} />
    </div>
  );
}

function Chip({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm border whitespace-nowrap transition-colors ${
        active ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-muted"
      }`}
      data-testid="cat-chip"
    >
      {children}
    </button>
  );
}

function CategoryDialog({ open, onOpenChange, onSaved }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name_tr: "", name_de: "" });
  const submit = async () => {
    try {
      await api.post("/categories", { name: { tr: form.name_tr, de: form.name_de }, sort: 0 });
      onOpenChange(false);
      setForm({ name_tr: "", name_de: "" });
      await onSaved();
      toast.success(t("save"));
    } catch (e) {
      toast.error("Error");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("newCategory")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Ad (TR)</Label>
            <Input value={form.name_tr} onChange={(e) => setForm({ ...form, name_tr: e.target.value })} className="mt-1" data-testid="cat-name-tr" />
          </div>
          <div>
            <Label>Name (DE)</Label>
            <Input value={form.name_de} onChange={(e) => setForm({ ...form, name_de: e.target.value })} className="mt-1" data-testid="cat-name-de" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={submit} data-testid="save-cat">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
